import {action, makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import {ChangeEvent, SyntheticEvent} from "react";
import type {IPoolPlayer} from "data/providers/api/tournament.provider";
import type {
	IFilter,
	IFilterForm,
	IPoolPlayersStore,
} from "data/stores/pool_players/pool_players.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IPlayerModalStore} from "data/stores/player_modal/player_modal.store";
import {PoolOrderedFields, SortOrder} from "data/enums";

export interface IPlayerPoolController extends ViewController {
	readonly i18n: ILocalizationStore;

	get filter(): IFilter;
	get players(): IPoolPlayer[];
	get fullTeam(): number[];
	get hasSearchValue(): boolean;

	handleFormOnChange: (event: ChangeEvent<IFilterForm>) => void;
	handleFormSubmit: (event: SyntheticEvent<IFilterForm>) => void;
	onResetSearch: () => void;
	setOrderBy: (value: PoolOrderedFields, direction?: SortOrder) => void;
	onAddPlayer: (playerId: number) => void;
	onRemovePlayer: (playerId: number) => void;
	openPlayerModal: (playerId: number) => void;
	getIsPlayerUsed3Times: (playerId: number) => boolean;
}

@injectable()
export class PlayerPoolController implements IPlayerPoolController {
	get filter() {
		return this._poolPlayersStore.filter;
	}

	get players() {
		return this._poolPlayersStore.sortedPlayers;
	}

	get fullTeam() {
		return this._teamStore.fullTeam;
	}

	get hasSearchValue() {
		return Boolean(this.filter.search.length);
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PlayersStore) public _playersStore: IPlayersStore,
		@inject(Bindings.PoolPlayersStore) public _poolPlayersStore: IPoolPlayersStore,
		@inject(Bindings.TournamentsStore) public _tournamentsStore: ITournamentsStore,
		@inject(Bindings.TeamStore) public _teamStore: ITeamStore,
		@inject(Bindings.PlayerModalStore) public _playerModalStore: IPlayerModalStore
	) {
		makeAutoObservable(this);
	}

	@action onAddPlayer = (playerId: number) => {
		this._teamStore.addPlayer(playerId);
	};

	@action onRemovePlayer = (playerId: number) => {
		this._teamStore.removePlayer(playerId);
	};

	@action handleFormOnChange = (event: ChangeEvent<IFilterForm>) => {
		const {search} = event.currentTarget;
		this._poolPlayersStore.setSearch(search.value);
	};

	@action handleFormSubmit = (event: SyntheticEvent<IFilterForm>) => {
		event.preventDefault();
	};

	@action onResetSearch = () => {
		this._poolPlayersStore.setSearch("");
	};

	@action setOrderBy = (value: PoolOrderedFields, direction: SortOrder = SortOrder.DESC) => {
		if (value === this._poolPlayersStore.filter.orderBy) {
			this._poolPlayersStore.setOrderDirection(value);
		} else {
			this._poolPlayersStore.setOrderBy(value);
			this._poolPlayersStore.setOrderDirection(value, direction);
		}
	};

	openPlayerModal = (playerId: number) => {
		this._playerModalStore.setPlayerId(playerId);
		this._playerModalStore.setPageArea("player_pool");
		this._playerModalStore.openPlayerModal();
	};

	getIsPlayerUsed3Times = (playerId: number) => {
		return this._tournamentsStore.getIsPlayerUsed3Times(playerId);
	};
}
